import SHA256 from "crypto-js/sha256";
import Hex from "crypto-js/enc-hex";

export const LAMBDA_PAYLOAD_FORMAT = "mea.lambdaPayload";
export const ASYNC_SAFE_BYTES = 900 * 1024;
export const SYNC_SAFE_BYTES = 5 * 1024 * 1024;

const encoder = new TextEncoder();
const SENSITIVE_FIELD_NAMES = new Set([
  "access_token", "accesstoken", "api_key", "apikey", "authorization",
  "client_secret", "clientsecret", "id_token", "idtoken", "password",
  "refresh_token", "refreshtoken",
]);

export function serializeLambdaPayload(payload) {
  if (typeof payload === "string") return payload;
  return JSON.stringify(payload ?? {});
}

export function getLambdaPayloadByteLength(payload) {
  return encoder.encode(serializeLambdaPayload(payload)).byteLength;
}

export function getLambdaPayloadLimit(invocationType) {
  return String(invocationType || "RequestResponse").toLowerCase() === "event"
    ? ASYNC_SAFE_BYTES
    : SYNC_SAFE_BYTES;
}

function assertEnvironment(environment) {
  const value = String(environment || "unknown").trim();
  if (!/^[A-Za-z0-9_-]+$/.test(value)) {
    throw new Error("Lambda payload environment contains unsupported characters.");
  }
  return value;
}

function assertOwnerId(ownerId) {
  const value = String(ownerId || "").trim();
  if (!/^[A-Za-z0-9:_-]+$/.test(value)) {
    throw new Error("Oversized Lambda payload cannot be staged without a valid authenticated identity.");
  }
  return value;
}

function assertFunctionName(functionName) {
  const raw = String(functionName || "").trim();
  const value = raw.includes(":function:") ? raw.split(":function:")[1].split(":")[0] : raw;
  if (!/^[A-Za-z0-9_-]+$/.test(value)) {
    throw new Error("Lambda payload target function contains unsupported characters.");
  }
  return value;
}

function splitSensitiveValues(serialized) {
  let parsed;
  try {
    parsed = JSON.parse(serialized);
  } catch {
    throw new Error("Oversized Lambda payload must be valid JSON before it can be staged safely.");
  }
  const inlineValues = [];
  function visit(value, path = []) {
    if (Array.isArray(value)) return value.map((item, index) => visit(item, [...path, index]));
    if (typeof value === "string") {
      try {
        const parsedValue = JSON.parse(value);
        if (parsedValue && typeof parsedValue === "object") {
          const inlineCountBeforeVisit = inlineValues.length;
          const visitedValue = visit(parsedValue, path);
          return inlineValues.length > inlineCountBeforeVisit ? JSON.stringify(visitedValue) : value;
        }
      } catch {
        // Ordinary strings are not nested JSON contracts.
      }
      return value;
    }
    if (!value || typeof value !== "object") return value;
    return Object.fromEntries(Object.entries(value).map(([key, child]) => {
      const childPath = [...path, key];
      if (SENSITIVE_FIELD_NAMES.has(key.toLowerCase())) {
        const index = inlineValues.push({ path: childPath, value: child }) - 1;
        return [key, { __meaInlineSensitive: index }];
      }
      return [key, visit(child, childPath)];
    }));
  }
  return { staged: JSON.stringify(visit(parsed)), inlineValues };
}

export async function prepareBrowserLambdaInvokeInput(input, options) {
  const serialized = serializeLambdaPayload(input?.Payload);
  const bytes = getLambdaPayloadByteLength(serialized);
  const limit = options?.maxBytes || getLambdaPayloadLimit(input?.InvocationType);
  if (bytes <= limit) return input;

  const bucketName = options?.bucketName;
  if (!bucketName || typeof options?.storagePut !== "function") {
    throw new Error("Oversized Lambda payload cannot be staged because browser storage is not configured.");
  }

  const environment = assertEnvironment(options.environment);
  const ownerId = assertOwnerId(options.ownerId);
  const functionName = assertFunctionName(input?.FunctionName);
  const now = options.now || Date.now();
  const expiresAt = new Date(now + Math.min(options.ttlMs || 60 * 60 * 1000, 60 * 60 * 1000)).toISOString();
  const { staged, inlineValues } = splitSensitiveValues(serialized);
  const stagedBytes = getLambdaPayloadByteLength(staged);
  const sha256 = SHA256(staged).toString(Hex);
  const objectId = options.createId ? options.createId() : crypto.randomUUID();
  const storageKey = `lambda-payloads/${environment}/v1/${functionName}/${new Date(now).toISOString().slice(0, 10)}/${objectId}.json`;
  const referenceKey = `protected/${ownerId}/${storageKey}`;

  const envelope = JSON.stringify({
    __meaLambdaPayload: {
      format: LAMBDA_PAYLOAD_FORMAT,
      version: 1,
      bucket: bucketName,
      key: referenceKey,
      bytes: stagedBytes,
      sha256,
      expiresAt,
      ...(inlineValues.length ? { inlineValues } : {}),
    },
  });
  if (getLambdaPayloadByteLength(envelope) > limit) {
    const error = new Error("Sensitive fields in an oversized Lambda request cannot fit safely in the invocation envelope.");
    error.name = "LambdaPayloadSensitiveFieldsTooLargeError";
    error.nonRetryable = true;
    throw error;
  }

  await options.storagePut(storageKey, staged, {
    level: "protected",
    contentType: "application/json",
    metadata: {
      format: LAMBDA_PAYLOAD_FORMAT,
      version: "1",
      sha256,
      bytes: String(stagedBytes),
      expiresat: expiresAt,
    },
  });

  return {
    ...input,
    Payload: envelope,
  };
}
